import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import config from '../../config';

import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  Divider,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  TrendingUp,
  AccountBalance,
  Receipt,
  Groups,
  Edit as EditIcon,
} from '@mui/icons-material';

// ─── Shared helpers (mirrors BookingReport) ───────────────────────────────────
const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const fmtDate = (value) => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ─── Summary Card ─────────────────────────────────────────────────────────────
function SummaryCard({ label, value, icon, color, sub }) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: '1 1 180px',
        minWidth: 160,
        p: 2,
        borderRadius: 3,
        border: `1.5px solid ${color}22`,
        bgcolor: `${color}0d`,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}>
      <Avatar sx={{ bgcolor: `${color}1a`, color, width: 42, height: 42 }}>
        {icon}
      </Avatar>
      <Box minWidth={0}>
        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.15 }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{sub}</Typography>
        )}
      </Box>
    </Paper>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AgentCommissionReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const currency = company?.currency ?? '₹';

  const API_URL = `${config.API_BASE_URL}/Home/GetAgentBookingCommsissionReport?companyId=${company?.id}`;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filters
  const [search, setSearch] = useState('');
  const [agentFilter, setAgentFilter] = useState('All');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 30;
  // Which agent rows are expanded to show their individual bookings.
  const [expandedAgents, setExpandedAgents] = useState({});

  // Edit an agent booking's commission figures.
  const [editBooking, setEditBooking] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    bookingPriceWithGST: '',
    propertyPrice: '',
    commission: '',
    source: '',
    agentName: '',
  });

  const openEdit = (r) => {
    setEditBooking(r);
    setEditForm({
      bookingPriceWithGST: r.bookingPriceWithGST ?? 0,
      propertyPrice: r.propertyPrice ?? 0,
      commission: r.commission ?? 0,
      source: r.source || 'OTA',
      agentName: r.agentName || '',
    });
  };

  const saveEdit = async () => {
    if (!editBooking) return;
    setEditSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${config.API_BASE_URL}/agentbooking/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          BookingId: editBooking.bookingId,
          AgentId: editBooking.agentId || 0,
          AgentName: editForm.agentName || '',
          Source: editForm.source || '',
          BookingPriceWithGST: Number(editForm.bookingPriceWithGST) || 0,
          PropertyPrice: Number(editForm.propertyPrice) || 0,
          Commission: Number(editForm.commission) || 0,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setSnackbar({
          open: true,
          message: d?.error || 'Update failed.',
          severity: 'error',
        });
        return;
      }
      setSnackbar({
        open: true,
        message: 'Agent booking updated.',
        severity: 'success',
      });
      setEditBooking(null);
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Update failed.',
        severity: 'error',
      });
    } finally {
      setEditSaving(false);
    }
  };

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ── Derived filter options ─────────────────────────────────────────────────
  const agentOptions = ['All', ...Array.from(new Set(rows.map((r) => r.agentMasterName).filter(Boolean)))];
  const sourceOptions = ['All', ...Array.from(new Set(rows.map((r) => r.source).filter(Boolean)))];

  // ── Filtered rows ──────────────────────────────────────────────────────────
  const filtered = rows.filter((r) => {
    const textMatch =
      String(r.bookingId || '').includes(search) ||
      (r.agentName || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.agentMasterName || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.bookingRefId || '').toLowerCase().includes(search.toLowerCase());
    const agentMatch = agentFilter === 'All' || r.agentMasterName === agentFilter;
    const sourceMatch = sourceFilter === 'All' || r.source === sourceFilter;
    // The date filter only applies to rows that actually carry a date. A
    // missing/invalid dateTime used to coerce to 1970, so any date range
    // silently dropped every undated row — blanking the table AND zeroing the
    // summary cards. Now undated rows are simply not excluded by the date range.
    const dRaw = r.dateTime ? new Date(r.dateTime) : null;
    const hasDate = dRaw && !Number.isNaN(dRaw.getTime());
    let fromMatch = true, toMatch = true;
    if (hasDate) {
      const d = new Date(dRaw);
      d.setHours(0, 0, 0, 0);
      if (fromDate) { const f = new Date(fromDate); f.setHours(0,0,0,0); fromMatch = d >= f; }
      if (toDate) { const t = new Date(toDate); t.setHours(23,59,59,999); toMatch = d <= t; }
    }
    return textMatch && agentMatch && sourceMatch && fromMatch && toMatch;
  });

  // ── Summary totals ─────────────────────────────────────────────────────────
  const totalBookings = filtered.length;
  const totalGST = filtered.reduce((s, r) => s + (r.bookingPriceWithGST || 0), 0);
  const totalProperty = filtered.reduce((s, r) => s + (r.propertyPrice || 0), 0);
  const totalCommission = filtered.reduce((s, r) => s + (r.commission || 0), 0);

  // ── Group the (date-filtered) bookings by agent ─────────────────────────────
  // The table shows one summary row per agent (count + totals); clicking it
  // expands the individual bookings underneath.
  const agentGroups = (() => {
    const map = new Map();
    for (const r of filtered) {
      const key = `${r.agentMasterName || r.agentName || 'Unknown'}||${r.agentId ?? ''}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          agentMasterName: r.agentMasterName || r.agentName || '—',
          agentName: r.agentName || '',
          source: r.source || '',
          count: 0,
          bookingTotal: 0,
          propertyTotal: 0,
          commissionTotal: 0,
          bookings: [],
        });
      }
      const g = map.get(key);
      g.count += 1;
      g.bookingTotal += r.bookingPriceWithGST || 0;
      g.propertyTotal += r.propertyPrice || 0;
      g.commissionTotal += r.commission || 0;
      g.bookings.push(r);
    }
    return Array.from(map.values()).sort((a, b) => b.commissionTotal - a.commissionTotal);
  })();

  const fmt = (n) => `${currency}${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!filtered.length) {
      setSnackbar({ open: true, message: 'No data to export', severity: 'warning' });
      return;
    }
    const exportData = filtered.map((r) => ({
      'Booking ID': r.bookingId,
      'Agent Master': r.agentMasterName,
      'Agent Name': r.agentName,
      'Source': r.source,
      'Booking Ref ID': r.bookingRefId,
      'Date & Time': fmtDateTime(r.dateTime),
      'Booking Price (with GST)': r.bookingPriceWithGST,
      'Property Price': r.propertyPrice,
      'Commission': r.commission,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Commission Report');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `Agent_Commission_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Table header style (mirrors BookingReport) ─────────────────────────────
  const thSx = {
    backgroundColor: '#1565c0',
    color: '#fff',
    fontWeight: 700,
    fontSize: '12px',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    border: '1px solid #1976d2',
    padding: '11px 12px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  };

  // ── Source badge ───────────────────────────────────────────────────────────
  const SourceBadge = ({ source }) => {
    const map = {
      OTA: { bg: '#e3f2fd', color: '#1565c0' },
      Direct: { bg: '#e8f5e9', color: '#2e7d32' },
      Agent: { bg: '#fff8e1', color: '#e65100' },
    };
    const style = map[source] || { bg: '#f3e5f5', color: '#6a1b9a' };
    return (
      <Chip
        label={source || '—'}
        size='small'
        sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: 11, height: 22 }}
      />
    );
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='70vh' flexDirection='column' gap={2}>
        <CircularProgress size={56} thickness={4} sx={{ color: '#1976d2' }} />
        <Typography color='text.secondary' fontSize={14}>Loading commission report...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.25, sm: 2, md: 2.5 }, bgcolor: '#f0f4f8', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant='h5' fontWeight='bold' color='#1565c0' letterSpacing={0.3} sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
            Agent Commission Report
          </Typography>
          <Typography fontSize={13} color='text.secondary' mt={0.3}>
            {filtered.length} record{filtered.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>
        <Button
          variant='contained'
          color='success'
          onClick={handleExport}
          size={isMobile ? 'small' : 'medium'}
          sx={{ fontWeight: 'bold', px: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: '0 2px 8px rgba(46,125,50,0.25)', textTransform: 'none', fontSize: { xs: 12, sm: 14 } }}>
          Export to Excel
        </Button>
      </Box>

      {/* ── Summary Cards ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
        <SummaryCard label='Total Bookings' value={totalBookings} icon={<Receipt fontSize='small' />} color='#1565c0' />
        <SummaryCard label='Booking Price (GST)' value={fmt(totalGST)} icon={<AccountBalance fontSize='small' />} color='#2e7d32' />
        <SummaryCard label='Property Price' value={fmt(totalProperty)} icon={<Groups fontSize='small' />} color='#e65100' />
        <SummaryCard label='Total Commission' value={fmt(totalCommission)} icon={<TrendingUp fontSize='small' />} color='#6a1b9a' sub={`Avg ${fmt(totalBookings ? totalCommission / totalBookings : 0)}/booking`} />
      </Box>

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e3eaf3', bgcolor: '#fff', display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          type='date'
          size='small'
          label='From Date'
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
          sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 150 }, flex: { xs: '1 1 calc(50% - 6px)', sm: 'unset' } }}
        />
        <TextField
          type='date'
          size='small'
          label='To Date'
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => { setToDate(e.target.value); setPage(0); }}
          sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 150 }, flex: { xs: '1 1 calc(50% - 6px)', sm: 'unset' } }}
        />
        <TextField
          label='Search Booking / Agent / Ref'
          variant='outlined'
          size='small'
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ minWidth: { xs: '100%', sm: 240 }, flex: { xs: '1 1 100%', sm: 'unset' } }}
        />
        <TextField
          select
          size='small'
          label='Agent'
          value={agentFilter}
          onChange={(e) => { setAgentFilter(e.target.value); setPage(0); }}
          sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 160 }, flex: { xs: '1 1 calc(50% - 6px)', sm: 'unset' } }}>
          {agentOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
        <TextField
          select
          size='small'
          label='Source'
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}
          sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 130 }, flex: { xs: '1 1 calc(50% - 6px)', sm: 'unset' } }}>
          {sourceOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
        <Button
          variant='outlined'
          color='secondary'
          onClick={() => { setFromDate(''); setToDate(''); setSearch(''); setAgentFilter('All'); setSourceFilter('All'); setPage(0); }}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: '1 1 100%', sm: 'unset' } }}>
          Clear
        </Button>
      </Paper>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e3eaf3', mb: 4 }}>
        <TableContainer sx={{ maxHeight: '72vh', overflowX: { xs: 'auto', md: 'hidden' } }}>
          <Table
            stickyHeader
            size='small'
            sx={{
              tableLayout: { xs: 'auto', md: 'fixed' },
              width: '100%',
              minWidth: { xs: 900, md: 'unset' },
              borderCollapse: 'collapse',
              '& th, & td': { border: '1px solid #e8edf3', padding: { xs: '6px 4px', sm: '10px 10px' }, textAlign: 'center', fontSize: { xs: '11px', sm: '13px' } },
              '& td': { bgcolor: 'background.paper', overflow: 'hidden' },
              '& tr:hover td': { bgcolor: '#e8f4ff !important' },
              '& tr:nth-of-type(even) td': { bgcolor: '#f7fafd' },
            }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thSx, width: '7%' }}>Booking ID</TableCell>
                <TableCell sx={{ ...thSx, width: '13%' }}>Agent Master</TableCell>
                <TableCell sx={{ ...thSx, width: '11%' }}>Agent Name</TableCell>
                <TableCell sx={{ ...thSx, width: '7%' }}>Source</TableCell>
                <TableCell sx={{ ...thSx, width: '9%' }}>Ref ID</TableCell>
                <TableCell sx={{ ...thSx, width: '14%' }}>Date & Time</TableCell>
                <TableCell sx={{ ...thSx, width: '13%', bgcolor: '#0d47a1 !important' }}>Booking Price (GST)</TableCell>
                <TableCell sx={{ ...thSx, width: '13%' }}>Property Price</TableCell>
                <TableCell sx={{ ...thSx, width: '13%', bgcolor: '#1b5e20 !important' }}>Commission</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {agentGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align='center' sx={{ py: 10 }}>
                    <Typography variant='h6' color='text.secondary'>No records found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                agentGroups
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((g) => {
                    const open = !!expandedAgents[g.key];
                    return (
                      <React.Fragment key={g.key}>
                        {/* ── Agent summary row (click to expand) ── */}
                        <TableRow
                          hover
                          onClick={() =>
                            setExpandedAgents((p) => ({ ...p, [g.key]: !p[g.key] }))
                          }
                          sx={{ cursor: 'pointer', '& td': { bgcolor: '#eef5ff !important' } }}>
                          {/* Expand toggle + booking count */}
                          <TableCell sx={{ textAlign: 'left !important' }}>
                            <Box display='flex' alignItems='center' gap={0.5}>
                              <Typography sx={{ fontSize: 15, color: '#1565c0', lineHeight: 1 }}>
                                {open ? '▾' : '▸'}
                              </Typography>
                              <Typography fontSize={12} fontWeight={700} color='#1565c0'>
                                {g.count} booking{g.count > 1 ? 's' : ''}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Agent Master */}
                          <TableCell sx={{ textAlign: 'left !important' }}>
                            <Box display='flex' alignItems='center' gap={0.75} minWidth={0}>
                              <Avatar sx={{ width: 26, height: 26, fontSize: 11, fontWeight: 'bold', bgcolor: '#bbdefb', color: '#1565c0', flexShrink: 0 }}>
                                {(g.agentMasterName || 'A').charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography fontSize={13} fontWeight={700} noWrap title={g.agentMasterName}>
                                {g.agentMasterName}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography fontSize={13} fontWeight={600} noWrap>{g.agentName || '—'}</Typography>
                          </TableCell>
                          <TableCell><SourceBadge source={g.source} /></TableCell>
                          <TableCell>—</TableCell>
                          <TableCell>—</TableCell>

                          {/* Totals */}
                          <TableCell sx={{ borderLeft: '2px solid #bbdefb' }}>
                            <Typography fontSize={13} fontWeight={800} color='#1565c0'>{fmt(g.bookingTotal)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={13} fontWeight={800}>{fmt(g.propertyTotal)}</Typography>
                          </TableCell>
                          <TableCell sx={{ borderLeft: '2px solid #c8e6c9' }}>
                            <Typography fontSize={13} fontWeight={800} color='#2e7d32'>{fmt(g.commissionTotal)}</Typography>
                          </TableCell>
                        </TableRow>

                        {/* ── Detail rows: individual bookings (when expanded) ── */}
                        {open && g.bookings.map((r, idx) => (
                          <TableRow key={`${g.key}-${r.bookingId}-${idx}`} sx={{ '& td': { bgcolor: '#ffffff !important' } }}>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: 12, color: '#1565c0' }}>
                              <Box display='flex' alignItems='center' gap={0.3} justifyContent='center'>
                                <span>#{r.bookingId}</span>
                                <IconButton
                                  size='small'
                                  onClick={() => openEdit(r)}
                                  title='Edit agent booking'
                                  sx={{ p: 0.2 }}>
                                  <EditIcon sx={{ fontSize: 15, color: '#1565c0' }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'left !important', pl: '34px !important' }}>
                              <Typography fontSize={12} color='text.secondary' noWrap>{r.agentMasterName || '—'}</Typography>
                            </TableCell>
                            <TableCell><Typography fontSize={12}>{r.agentName || '—'}</Typography></TableCell>
                            <TableCell><SourceBadge source={r.source} /></TableCell>
                            <TableCell><Typography fontSize={11} color='text.secondary'>{r.bookingRefId || '—'}</Typography></TableCell>
                            <TableCell>
                              <Box sx={{ lineHeight: 1.25 }}>
                                <Typography component='span' sx={{ display: 'block', fontSize: 11, fontWeight: 500 }}>{fmtDate(r.dateTime)}</Typography>
                                <Typography component='span' sx={{ display: 'block', fontSize: 10, color: 'text.secondary' }}>
                                  {new Date(r.dateTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ borderLeft: '2px solid #bbdefb' }}>
                              <Typography fontSize={12} fontWeight={600} color='#1565c0'>{fmt(r.bookingPriceWithGST)}</Typography>
                            </TableCell>
                            <TableCell><Typography fontSize={12} fontWeight={500}>{fmt(r.propertyPrice)}</Typography></TableCell>
                            <TableCell sx={{ borderLeft: '2px solid #c8e6c9' }}>
                              <Typography fontSize={12} fontWeight={600} color='#2e7d32'>{fmt(r.commission)}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })
              )}
            </TableBody>

            {/* ── Totals footer row ────────────────────────────────────────── */}
            {filtered.length > 0 && (
              <TableBody>
                <TableRow sx={{ bgcolor: '#e8f0fe !important' }}>
                  <TableCell colSpan={6} sx={{ fontWeight: 700, fontSize: 13, color: '#1565c0', textAlign: 'right !important', bgcolor: '#e8f0fe !important', border: '1px solid #c5d8f8 !important' }}>
                    TOTALS ({filtered.length} bookings)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 13, color: '#1565c0', bgcolor: '#e8f0fe !important', border: '1px solid #c5d8f8 !important', borderLeft: '2px solid #90caf9 !important' }}>
                    {fmt(totalGST)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 13, bgcolor: '#e8f0fe !important', border: '1px solid #c5d8f8 !important' }}>
                    {fmt(totalProperty)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 13, color: '#2e7d32', bgcolor: '#e8f5e9 !important', border: '1px solid #a5d6a7 !important', borderLeft: '2px solid #81c784 !important' }}>
                    {fmt(totalCommission)}
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ bgcolor: '#fff', borderTop: '1px solid #e8edf3' }}>
          <TablePagination
            rowsPerPageOptions={[30]}
            component='div'
            count={agentGroups.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            labelRowsPerPage='Rows per page:'
            sx={{
              '.MuiTablePagination-toolbar': { fontSize: '13px' },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '13px' },
            }}
          />
        </Box>
      </Paper>

      {/* ── Agent-wise summary breakdown ──────────────────────────────────── */}
      {filtered.length > 0 && (() => {
        const grouped = {};
        filtered.forEach((r) => {
          const key = r.agentMasterName || 'Unknown';
          if (!grouped[key]) grouped[key] = { count: 0, gst: 0, property: 0, commission: 0 };
          grouped[key].count += 1;
          grouped[key].gst += r.bookingPriceWithGST || 0;
          grouped[key].property += r.propertyPrice || 0;
          grouped[key].commission += r.commission || 0;
        });
        const entries = Object.entries(grouped).sort((a, b) => b[1].commission - a[1].commission);
        return (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e3eaf3', bgcolor: '#fff', mb: 4 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1565c0', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.5 }}>
              Agent-Wise Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {entries.map(([agent, data]) => (
                <Paper key={agent} elevation={0} sx={{ flex: '1 1 220px', minWidth: 200, p: 1.75, borderRadius: 2, border: '1px solid #e3eaf3', bgcolor: '#f7fafd' }}>
                  <Box display='flex' alignItems='center' gap={1} mb={1}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#bbdefb', color: '#1565c0', fontSize: 12, fontWeight: 700 }}>
                      {agent.charAt(0)}
                    </Avatar>
                    <Typography fontWeight={700} fontSize={13} noWrap>{agent}</Typography>
                  </Box>
                  <Box display='flex' justifyContent='space-between' mb={0.4}>
                    <Typography fontSize={12} color='text.secondary'>Bookings</Typography>
                    <Typography fontSize={12} fontWeight={600}>{data.count}</Typography>
                  </Box>
                  <Box display='flex' justifyContent='space-between' mb={0.4}>
                    <Typography fontSize={12} color='text.secondary'>Booking Price</Typography>
                    <Typography fontSize={12} fontWeight={600} color='#1565c0'>{fmt(data.gst)}</Typography>
                  </Box>
                  <Box display='flex' justifyContent='space-between' mb={0.4}>
                    <Typography fontSize={12} color='text.secondary'>Property Price</Typography>
                    <Typography fontSize={12} fontWeight={600}>{fmt(data.property)}</Typography>
                  </Box>
                  <Divider sx={{ my: 0.75 }} />
                  <Box display='flex' justifyContent='space-between'>
                    <Typography fontSize={12} color='text.secondary' fontWeight={600}>Commission</Typography>
                    <Typography fontSize={13} fontWeight={800} color='#2e7d32'>{fmt(data.commission)}</Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Paper>
        );
      })()}

      {/* ── Edit agent booking dialog ─────────────────────────────────────── */}
      <Dialog
        open={!!editBooking}
        onClose={() => !editSaving && setEditBooking(null)}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Edit Agent Booking
          {editBooking ? ` — #${editBooking.bookingId}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Box display='flex' flexDirection='column' gap={2} sx={{ mt: 0.5 }}>
            <TextField
              label='Agent Name'
              size='small'
              fullWidth
              value={editForm.agentName}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, agentName: e.target.value }))
              }
            />
            <TextField
              select
              label='Source'
              size='small'
              fullWidth
              value={editForm.source}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, source: e.target.value }))
              }>
              <MenuItem value='OTA'>OTA</MenuItem>
              <MenuItem value='Agent'>Agent</MenuItem>
              <MenuItem value='Direct'>Direct</MenuItem>
            </TextField>
            <TextField
              label='Booking Price (with GST)'
              size='small'
              type='number'
              fullWidth
              value={editForm.bookingPriceWithGST}
              onChange={(e) =>
                setEditForm((f) => ({
                  ...f,
                  bookingPriceWithGST: e.target.value,
                }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{currency}</InputAdornment>
                ),
              }}
            />
            <TextField
              label='Property Price'
              size='small'
              type='number'
              fullWidth
              value={editForm.propertyPrice}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, propertyPrice: e.target.value }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{currency}</InputAdornment>
                ),
              }}
            />
            <TextField
              label='Commission'
              size='small'
              type='number'
              fullWidth
              value={editForm.commission}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, commission: e.target.value }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{currency}</InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditBooking(null)} disabled={editSaving}>
            Cancel
          </Button>
          <Button variant='contained' onClick={saveEdit} disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ──────────────────────────────────────────────────────── */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} variant='filled'>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}